/* eslint-disable no-unused-vars */
import { useMemo } from 'react';
import { getUserInfo } from '../utils';

/**
 * Custom hook for handling user permissions
 * Currently supports role-based permissions, with structure ready for future section-specific permissions
 * 
 * @param {string} section - Optional section name for future section-specific permissions
 * @returns {Object} Object containing permission check functions: { canSee, canEdit, canCreate, canDelete }
 */
export const usePermissions = (section = null) => {
	const userInfo = getUserInfo();

	// Role-based permissions configuration
	// Structure allows for future section-specific permissions
	const rolePermissions = useMemo(() => {
		if (!userInfo) {
			return {
				canSee: false,
				canEdit: false,
				canCreate: false,
				canDelete: false,
			};
		}

		const role = userInfo.role;
		const isAdmin = userInfo.isAdmin;

		// Super Admin: Full access to everything
		if (role === 'super_admin' || (isAdmin && role === 'super_admin')) {
			return {
				canSee: true,
				canEdit: true,
				canCreate: true,
				canDelete: true,
			};
		}

		// Admin: Can see, edit, create CANNOT delete 
		if (role === 'admin' || (isAdmin && !role)) {
			return {
				canSee: true,
				canEdit: true,
				canCreate: true,
				canDelete: false,
			};
		}

		// Manager: Can see and create  , but cannot edit or delete
		if (role === 'manager') {
			return {
				canSee: true,
				canEdit: false,
				canCreate: true,
				canDelete: false,
			};
		}

		// Employee: Read-only access
		if (role === 'employee' || !role) {
			return {
				canSee: true,
				canEdit: false,
				canCreate: false,
				canDelete: false,
			};
		}

		// Default: No permissions
		return {
			canSee: false,
			canEdit: false,
			canCreate: false,
			canDelete: false,
		};
	}, [userInfo]);

	// Future: Section-specific permissions
	// This structure allows for per-section permissions later
	// Example: sectionPermissions[section]?.[role] || rolePermissions
	const sectionPermissions = useMemo(() => {
		// TODO: Implement section-specific permissions
		// Example structure:
		// const sectionPermissionsConfig = {
		//   'users': {
		//     'admin': { canSee: true, canEdit: true, canCreate: true },
		//     'manager': { canSee: false, canEdit: false, canCreate: false },
		//   },
		//   'accounts': {
		//     'manager': { canSee: true, canEdit: true, canCreate: true },
		//   },
		// };
		// return section ? (sectionPermissionsConfig[section]?.[userInfo?.role] || rolePermissions) : rolePermissions;
		
		// For now, return role-based permissions
		return rolePermissions;
	}, [rolePermissions, section]);

	/**
	 * Check if user can see/view content
	 * @param {string} specificSection - Optional specific section to check (for future use)
	 * @returns {boolean}
	 */
	const canSee = (specificSection = null) => {
		// Future: Check section-specific permissions if provided
		// For now, return role-based permission
		return sectionPermissions.canSee;
	};

	/**
	 * Check if user can edit content
	 * @param {string} specificSection - Optional specific section to check (for future use)
	 * @returns {boolean}
	 */
	const canEdit = (specificSection = null) => {
		// Future: Check section-specific permissions if provided
		// For now, return role-based permission
		return sectionPermissions.canEdit;
	};

	/**
	 * Check if user can create content
	 * @param {string} specificSection - Optional specific section to check (for future use)
	 * @returns {boolean}
	 */
	const canCreate = (specificSection = null) => {
		// Future: Check section-specific permissions if provided
		// For now, return role-based permission
		return sectionPermissions.canCreate;
	};

	/**
	 * Check if user can delete content
	 * @param {string} specificSection - Optional specific section to check (for future use)
	 * @returns {boolean}
	 */
	const canDelete = (specificSection = null) => {
		// Future: Check section-specific permissions if provided
		// For now, return role-based permission
		return sectionPermissions.canDelete;
	};

	return {
		canSee,
		canEdit,
		canCreate,
		canDelete,
		// Expose user info for convenience
		userInfo,
		// Expose role for convenience
		role: userInfo?.role || (userInfo?.isAdmin ? 'admin' : 'employee'),
	};
};

